/**
 * Customer profile · "Salik & parking" tab: the Salik trips, Darb (Abu Dhabi) trips and
 * parking of each of the customer's contracts, from the engine database (salik.db) - every
 * car the contract held, each within its own period - with each row's state on Ejar (the
 * engine reconciles with Ejar on every read).
 * The states and the money are The Engine's own (the-engine/customerDue.logic - owner, 28 Sep 2026):
 * "On Ejar" only when Ejar really holds the line, "Unlimited Salik" for a trip the contract's
 * package covers (never "On Ejar"), else "Not on Ejar"; a trip is charged its gate + AED 1.05, a covered
 * trip nothing (Branch charges' 0.00). A contract the engine could not match to a car shows no figure.
 * Every contract of the customer can be picked (none hidden without saying so), and the whole
 * customer's "not on Ejar yet" adds up the contracts read, saying how many that is.
 * "Pull missing days" completes the days never pulled from the portal; "Refresh live" reads the
 * contract's Salik trips again over its whole time, car by car (Salik posts some trips a day late), and
 * today's Darb and parking.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { finesService, errorMessage } from '../services/finesService';
import { followJob } from '../services/followJob';
import DataTable from '../ui/DataTable/DataTable';
import { CellPlate, CellMoney, CellStatus, CellText, CellTag, CellMuted } from '../ui/DataTable/cells';
import Stat, { StatGrid } from '../ui/Stat/Stat';
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import { ICONS } from '../ui/icon-map';
import EmptyState from '../ui/EmptyState';
import LoadingSpinner from './LoadingSpinner';
import { formatPlateNumber } from '../utils/plateFormatter';
import { confirmDialog } from '../ui/feedback';
import {
    KINDS, mergeKinds, settleKinds, unlimitedOfAnswer, unmatchedOf, rowStatusOf, amountOf, chargeOf, contractFigures, customerTotals,
} from './customerCharges.logic';
import { formatMoney, TRIP_SURCHARGE } from '../constants/finance';
import { salikTotalWords, tripFeeOf } from '../pages/the-engine/customerDue.logic';
import { isOpenStatus } from '../services/contactsService';

const when = (r) => String(r.trip_time || r.entry_time || '').replace('T', ' ').slice(0, 16);
const where = (r) => (r._kind === 'parking'
    ? [r.location, r.zone].filter(Boolean).join(' · ')
    : [r.gate, r.direction].filter(Boolean).join(' → '));
// the one money format (DUP-27): 2 decimals, en-AE grouping, AED first, empty -> —
const money = (n) => formatMoney(n);
// Ejar's "Open" only: an empty / unknown contacts status is not an open contract
const isOpen = isOpenStatus;
// The contract buttons shown before "Show all": the rest are one click away and the button says how many
// (owner, 28 Sep 2026 - the tab used to show the first 10 contracts and hide the rest without a word).
export const CHIPS_SHOWN = 12;
const READ_AT_ONCE = 2;          // contracts read together by "Read the other contracts" (each reads Ejar once)
const NO_FIGURES = contractFigures([], null);

export default function CustomerCharges({ contracts, canPull }) {
    // open contracts first, then the newest (the list comes newest first) - every one of them
    const choices = useMemo(() => {
        const list = (contracts || []).filter((r) => r.c);
        return [...list.filter((r) => isOpen(r.s)), ...list.filter((r) => !isOpen(r.s))];
    }, [contracts]);
    const [showAll, setShowAll] = useState(false);
    const [picked, setPicked] = useState(null);
    const contract = picked || choices[0]?.c || '';
    const [data, setData] = useState({});        // contract -> { rows, missing, ejarChecked, note, unl, failed, unmatched, figures }
    const [loading, setLoading] = useState(false);
    const [reading, setReading] = useState(null); // { done, of } while the other contracts are read
    const [job, setJob] = useState(null);         // { stage } while a portal pull runs
    const [err, setErr] = useState('');
    const [fresh, setFresh] = useState(null);     // { agoMin, by } when the engine says it was just pulled
    const alive = useRef(true);
    useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

    // one call for the three kinds: the engine reads the contract's Ejar charges once, not three times; its answer
    // carries the contract's Unlimited Salik answer (the reconcile's read, else THE rule) for the trip states
    const fetchOne = useCallback(async (cn) => {
        let answer = null;
        let error = null;
        try {
            answer = await finesService.salikContractRows({ contract: cn, status: 'all' });
        } catch (e) { error = e; }
        const { rows, missing, ejarChecked, note, errors } = mergeKinds(settleKinds(answer, error), errorMessage);
        const unl = error ? null : unlimitedOfAnswer(answer);
        // a contract that could not be read has no figures: it is never counted as "nothing owed"; nor has one the engine
        // could not match to its car(s) - its trips are UNKNOWN, never a green "0 not on Ejar yet" (review, owner 28 Sep 2026)
        const unmatched = error ? '' : unmatchedOf(answer);
        const figures = error || unmatched ? null : contractFigures(rows, unl, tripFeeOf(answer, TRIP_SURCHARGE), answer?.salikTotal || null);   // staff: no trip fee
        return { cn, errors, entry: { rows, missing, ejarChecked, note, unl, failed: !!error, unmatched, figures } };
    }, []);

    const load = useCallback(async (cn) => {
        if (!cn) return;
        setLoading(true); setErr('');
        const { entry, errors } = await fetchOne(cn);
        if (!alive.current) return;
        setData((m) => ({ ...m, [cn]: entry }));
        if (errors.length) setErr(errors.join(' · '));
        setLoading(false);
    }, [fetchOne]);

    useEffect(() => { if (contract && !data[contract]) load(contract); }, [contract, data, load]);

    // The whole customer: every contract not read yet (or whose read failed), a couple at a time
    const readRest = async () => {
        // a contract not matched to a car was read: its answer is known ("not matched"), reading it again changes nothing
        const todo = choices.map((r) => r.c).filter((cn) => !(data[cn] && (data[cn].figures || data[cn].unmatched)));
        if (!todo.length) return;
        setErr('');
        setReading({ done: 0, of: todo.length });
        const failed = [];
        for (let i = 0; i < todo.length; i += READ_AT_ONCE) {
            const got = await Promise.all(todo.slice(i, i + READ_AT_ONCE).map(fetchOne));
            if (!alive.current) return;
            setData((m) => { const next = { ...m }; got.forEach((g) => { next[g.cn] = g.entry; }); return next; });
            got.forEach((g) => { if (g.entry.failed) failed.push(g.cn); });
            setReading({ done: Math.min(i + READ_AT_ONCE, todo.length), of: todo.length });
        }
        setReading(null);
        if (failed.length) setErr(`${failed.length} contract${failed.length === 1 ? '' : 's'} could not be read (${failed.join(', ')}) - not in the customer's total.`);
    };

    // Live pulls follow one rule on the engine: the same contract at the same time is ONE pull
    // (the second asker joins it), again within 10 minutes it is answered "fresh", anything else
    // waits in line behind the pull that is running.
    const pull = async (force, again = false) => {
        if (!(await confirmDialog({
            title: force ? 'Refresh live?' : 'Pull the missing days?',
            message: force ? `Reads contract ${contract}'s Salik trips again for its whole time (its cars), and today's Darb and parking, from the portals.`
                : `Reads from the portals the days of contract ${contract} that were never pulled.`,
            confirmLabel: 'Yes, pull now',
        }))) return;
        setErr(''); setFresh(null);
        try {
            let j = await finesService.salikSync({ kind: 'both', contract, ...(force ? { force: true } : {}), ...(again ? { again: true } : {}) });
            if (j && j.stage === 'fresh') { setFresh(j.fresh || {}); return; }
            setJob(j);
            if (j && j.status === 'running') {
                const { jobId } = j;
                j = await followJob(() => finesService.salikSyncJob(jobId), { intervalMs: 2500, onUpdate: setJob });
            }
            if (j && j.status === 'error') setErr(j.result?.error || 'The pull failed');
        } catch (e) {
            setErr(errorMessage(e));
        } finally {
            setJob(null);
            await load(contract);
        }
    };

    const figuresOf = useMemo(() => {
        const out = {};
        Object.entries(data).forEach(([cn, entry]) => { if (entry && entry.figures) out[cn] = entry.figures; });
        return out;
    }, [data]);
    const unmatchedRead = useMemo(() => {
        const out = {};
        Object.entries(data).forEach(([cn, entry]) => { if (entry && entry.unmatched) out[cn] = entry.unmatched; });
        return out;
    }, [data]);

    if (!choices.length) return <EmptyState icon={ICONS.fines} title="No contract to read Salik and parking for." />;
    const d = data[contract];
    const rows = d?.rows || [];
    const f = d?.figures || NO_FIGURES;
    // a contract whose read failed, or that the engine could not match to its car(s), shows no figure at all - never a
    // "0 not on Ejar" it does not know
    const unknown = !!(d?.failed || d?.unmatched);
    const shownN = (n) => (unknown ? '—' : n);
    const shownAed = (aed) => (d?.failed ? 'not read' : d?.unmatched ? 'not matched' : money(aed));
    const notOn = f.states.not_on_ejar;
    const covered = f.states.unlimited;
    const whole = customerTotals(choices.map((r) => r.c), figuresOf, unmatchedRead);
    const shown = showAll || choices.length <= CHIPS_SHOWN ? choices : choices.slice(0, CHIPS_SHOWN);
    const hidden = choices.length - shown.length;
    const { left } = whole;

    const columns = [
        { key: '_kind', header: 'Type', cell: (r) => { const k = KINDS.find((x) => x.kind === r._kind); return <CellTag tone={k?.tone}>{k?.label}</CellTag>; } },
        { key: 'when', header: 'Date & time', nowrap: true, sortValue: when, cell: (r) => <span className="ds-cell-date">{when(r) || '—'}</span> },
        { key: 'plate', header: 'Plate', nowrap: true, cell: (r) => <CellPlate>{formatPlateNumber(r.plate || '')}</CellPlate> },
        { key: 'where', header: 'Where', searchable: false, cell: (r) => <CellText max={140}>{where(r)}</CellText> },
        // the row's own amount (the gate / the parking), then what the customer is charged - Branch charges' two
        // columns: a trip = the gate + AED 1.05, a trip covered by Unlimited Salik 0.00 (review, owner 28 Sep 2026)
        { key: 'amount', header: 'Amount', sortValue: amountOf, cell: (r) => <CellMoney value={amountOf(r)} /> },
        { key: 'charged', header: 'Charged', sortValue: (r) => chargeOf(r, d?.unl), cell: (r) => <CellMoney value={chargeOf(r, d?.unl)} /> },
        {
            key: 'reg_status', header: 'Ejar', sortValue: (r) => rowStatusOf(r, d?.unl).label,
            cell: (r) => {
                const s = rowStatusOf(r, d?.unl);
                return (
                    <span className="inline-flex flex-col items-center">
                        <CellStatus status={s.label} label={s.label} tone={s.variant} />
                        {String(r.reg_note || '').startsWith('late - ') && <CellMuted>{r.reg_note}</CellMuted>}
                    </span>
                );
            },
        },
    ];

    return (
        <div className="flex flex-col gap-3">
            {choices.length > 1 && (
                <div role="status" className="flex flex-wrap items-center gap-2 text-sm border border-gray-200 bg-gray-50 rounded-lg px-3 py-2">
                    <span className="font-semibold text-gray-900">Whole customer</span>
                    <span className="text-gray-700">
                        {whole.complete ? `all ${whole.total} contracts` : `${whole.read} of ${whole.total} contracts read`}
                        {': '}
                        <span className={whole.notOnEjarN ? 'font-semibold text-red-700' : 'text-green-800'}>
                            {whole.notOnEjarN} not on Ejar yet · {money(whole.notOnEjarAed)}
                        </span>
                        {whole.unlimitedN ? ` · ${whole.unlimitedN} trip${whole.unlimitedN === 1 ? '' : 's'} covered by Unlimited Salik` : ''}
                        {whole.unmatched ? ` · ${whole.unmatched} contract${whole.unmatched === 1 ? '' : 's'} not matched to a car - Salik, Darb and parking not known` : ''}
                        {!whole.complete && ' - the total counts the contracts read only.'}
                    </span>
                    <span className="flex-1" />
                    {left > 0 && (
                        <Button size="xs" variant="secondary" disabled={!!reading || loading} isLoading={!!reading} onClick={readRest}
                            title="Reads the Salik, Darb and parking of every contract of this customer not read yet">
                            {reading ? `Reading ${reading.done} of ${reading.of}…` : `Read the other ${left} contract${left === 1 ? '' : 's'}`}
                        </Button>
                    )}
                </div>
            )}

            <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Contract">
                {shown.map((r) => (
                    <button key={r.c} type="button" onClick={() => setPicked(r.c)} aria-pressed={contract === r.c}
                        className={`yelo-badge yelo-badge--${contract === r.c ? 'accent' : 'neutral'} ds-chip ${contract === r.c ? 'is-active' : ''}`}>
                        <span className="font-mono">{r.c}</span>{isOpen(r.s) && <span className="text-green-700">· open</span>}
                    </button>
                ))}
                {choices.length > CHIPS_SHOWN && (
                    <Button size="xs" variant="ghost" onClick={() => setShowAll((v) => !v)}>
                        {hidden ? `Show all ${choices.length} contracts (${hidden} more)` : 'Show fewer contracts'}
                    </Button>
                )}
            </div>

            {loading && !d ? (
                <LoadingSpinner message="Reading Salik, Darb and parking…" />
            ) : (
                <>
                    <StatGrid min={140}>
                        {KINDS.map((k) => (
                            <Stat key={k.kind} label={k.label} value={shownN(f.kinds[k.kind].n)} meta={shownAed(f.kinds[k.kind].charge)}
                                title={k.kind === 'parking' ? undefined : 'Charged: the gate + AED 1.05 a trip (company staff: the gate only); nothing for a trip covered by Unlimited Salik'} />
                        ))}
                        <Stat label="Not on Ejar yet" value={shownN(notOn.n)} tone={unknown ? undefined : notOn.n ? 'danger' : 'success'} meta={shownAed(notOn.aed)}
                            title="Salik and Darb at the gate + AED 1.05 a trip (company staff: the gate only), and parking - what Ejar does not hold yet" />
                        {(f.unlimited || covered.n > 0) && (
                            <Stat label="Unlimited Salik" value={covered.n} meta={covered.n === 1 ? 'trip covered by the package' : 'trips covered by the package'} />
                        )}
                    </StatGrid>

                    <div className="flex flex-wrap items-center gap-2">
                        {!unknown && f.salikTotal && (
                            // everything Ejar holds for Salik against every trip, whatever the date (owner, 1 Oct 2026)
                            <span className={`text-sm font-medium ${Math.abs(f.salikTotal.diff) > 0.05 ? 'text-amber-700' : 'text-green-700'}`}>
                                All {f.salikTotal.darbTrips ? 'Salik + Darb' : 'Salik'} on Ejar {money(f.salikTotal.ejar)} for {f.salikTotal.trips} trip(s) worth {money(f.salikTotal.expected)}: {salikTotalWords(f.salikTotal, money)}
                            </span>
                        )}
                        {d?.missing > 0 && (
                            <span className="text-sm text-amber-700">{d.missing} day{d.missing === 1 ? '' : 's'} of this contract were never pulled from Salik.</span>
                        )}
                        {d && d.ejarChecked === false && <span className="text-sm text-gray-600">Ejar could not be checked right now: the statuses are from the database.</span>}
                        <span className="flex-1" />
                        {canPull && (
                            <>
                                {d?.missing > 0 && (
                                    <Button size="sm" variant="primary" disabled={!!job} onClick={() => pull(false)} leadingIcon={<Icon glyph={ICONS.refresh} size="sm" />}>Pull missing days</Button>
                                )}
                                <Button size="sm" disabled={!!job} isLoading={!!job} onClick={() => pull(true)} leadingIcon={<Icon glyph={ICONS.refresh} size="sm" />}
                                    title="Reads this contract's Salik trips again for its whole time (its cars), and today's Darb and parking, live from the portals">
                                    {job ? (job.stage || 'Pulling…') : 'Refresh live'}
                                </Button>
                            </>
                        )}
                    </div>
                    {err && <div role="alert" className="text-sm text-red-700">{err}</div>}
                    {fresh && (
                        <div role="status" className="flex flex-wrap items-center gap-2 text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                            Already live: pulled {fresh.agoMin ? `${fresh.agoMin} min ago` : 'less than a minute ago'}{fresh.by ? ` by ${fresh.by}` : ''} - these figures are current.
                            <Button size="xs" variant="ghost" onClick={() => pull(true, true)}>Pull again anyway</Button>
                        </div>
                    )}
                    {d?.note && !rows.length && !d?.unmatched && <div className="text-sm text-gray-600">{d.note}</div>}

                    {d?.failed ? (
                        <EmptyState icon={ICONS.error} title="This contract's Salik, Darb and parking could not be read." />
                    ) : d?.unmatched ? (
                        <EmptyState icon={ICONS.warning} title="This contract could not be matched to a car: its Salik, Darb and parking are not known."
                            description={`The engine says: ${d.unmatched}.`} />
                    ) : rows.length === 0 ? (
                        <EmptyState icon={ICONS.fines} title="No Salik, Darb or parking on this contract." />
                    ) : (
                        <DataTable
                            columns={columns}
                            data={rows}
                            rowKey={(r, i) => `${r._kind}${r.id || ''}${i}`}
                            searchable={false}
                            pagination={false}
                            columnToggle={false}
                            maxHeight="45vh"
                        />
                    )}
                </>
            )}
        </div>
    );
}
